import React, {
	Dispatch,
	ReactNode,
	useCallback,
	useEffect,
	useState,
} from 'react';

import { TagPicker } from '@/components/Layout/components/SongModalPanel/TagPicker/TagPicker';
import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import { ComingSoon } from '@/components/shared/ComingSoon';
import {
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import AsaphDialog from '@/components/shared/Dialog/AsaphDialog';
import {
	ENERGY_TAGS,
	GOSPEL_TAGS,
	PANEL_TRANSITION_TIMEOUT,
	SONG_TYPE_TAGS,
} from '@/config/constants';
import { TeamSong } from '@/graphql';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { Fade, Slide } from '@material-ui/core';
import { isNil, pathOr } from 'ramda';

import styles from './AddCustomSong.module.scss';
import { AddCustomSongRoot } from './AddCustomSongRoot';
import { AppleMusicUrl } from './AppleMusicUrl';
import { Lyrics } from './Lyrics';
import { RecordedKey } from './RecordedKey';
import { SpotifyUrl } from './SpotifyUrl';
import { YouTubeUrl } from './YouTubeUrl';

export enum EAddCustomSongDisplayState {
	ROOT,
	SPOTIFY_URL,
	YOUTUBE_URL,
	APPLE_MUSIC_URL,
	SONG_SETTINGS,
	RECORDED_KEY,
	ROTATION_STATUS,
	CUSTOM_DEFAULT_KEY,
	LYRICS,
	ENERGY_TAG,
	SONG_TYPE_TAG,
}

interface IProps {
	onClose: () => void;
}

export type CreateTeamSong = Partial<Omit<TeamSong, '__typename'>>;

export interface AddCustomSongDisplayProps {
	pushDisplayState: (displayState: EAddCustomSongDisplayState) => void;
	popDisplayState: () => void;
	panelTransitionTimeout: number;
	setOpen: (value: boolean) => void;
	teamSong: CreateTeamSong;
	setTeamSong: React.Dispatch<React.SetStateAction<CreateTeamSong>>;
	setCustomTitle: Dispatch<string>;
	handleCompleteClose: () => void;
}

const displayStateComponents: Map<
	EAddCustomSongDisplayState,
	{
		title?: string;
		component: React.FC<AddCustomSongDisplayProps>;
	}
> = new Map([
	[
		EAddCustomSongDisplayState.ROOT,
		{ title: 'Create new song', component: AddCustomSongRoot },
	],
	[
		EAddCustomSongDisplayState.SPOTIFY_URL,
		{
			title: 'Spotify URL',
			component: ({ teamSong, setTeamSong, popDisplayState }) => {
				return (
					<SpotifyUrl
						teamSong={teamSong}
						onSubmit={({ spotifyId }) => {
							setTeamSong((state) => ({
								...state,
								spotifyId,
							}));
							popDisplayState();
						}}
					/>
				);
			},
		},
	],
	[
		EAddCustomSongDisplayState.YOUTUBE_URL,
		{
			title: 'YouTube URL',
			component: ({ teamSong, setTeamSong, popDisplayState }) => {
				return (
					<YouTubeUrl
						teamSong={teamSong}
						onSubmit={({ youtubeId }) => {
							setTeamSong((state) => ({
								...state,
								youtubeId,
							}));
							popDisplayState();
						}}
					/>
				);
			},
		},
	],
	[
		EAddCustomSongDisplayState.RECORDED_KEY,
		{
			title: 'Recorded key',
			component: RecordedKey,
		},
	],
	[
		EAddCustomSongDisplayState.APPLE_MUSIC_URL,
		{
			title: 'Apple Music URL',
			component: ({ teamSong, setTeamSong, popDisplayState }) => {
				return (
					<AppleMusicUrl
						teamSong={teamSong}
						onSubmit={({ appleMusicId }) => {
							setTeamSong((state) => ({
								...state,
								appleMusicId,
							}));
							popDisplayState();
						}}
					/>
				);
			},
		},
	],
	[
		EAddCustomSongDisplayState.CUSTOM_DEFAULT_KEY,
		{
			component: ComingSoon,
		},
	],
	[
		EAddCustomSongDisplayState.LYRICS,
		{
			title: 'Song Lyrics',
			component: ({ teamSong, setTeamSong, popDisplayState }) => {
				return (
					<Lyrics
						teamSong={teamSong}
						onSubmit={({ lyrics }) => {
							setTeamSong((state) => ({
								...state,
								lyrics,
							}));
							popDisplayState();
						}}
					/>
				);
			},
		},
	],
	[
		EAddCustomSongDisplayState.ENERGY_TAG,
		{
			title: 'Energy',
			component: ({ teamSong, setTeamSong, popDisplayState }) => (
				<TagPicker
					label="Select the energy level for"
					songName={teamSong.name}
					items={ENERGY_TAGS}
					selected={teamSong.energyTags ?? []}
					multiSelect={false}
					onSave={(tags) => {
						setTeamSong((state) => ({
							...state,
							energyTags: tags,
						}));
						popDisplayState();
					}}
				/>
			),
		},
	],
	[
		EAddCustomSongDisplayState.SONG_TYPE_TAG,
		{
			title: 'Song Type',
			component: ({ teamSong, setTeamSong, popDisplayState }) => (
				<TagPicker
					label="Select the song type for"
					songName={teamSong.name}
					items={SONG_TYPE_TAGS}
					selected={teamSong.songTypeTags ?? []}
					multiSelect={false}
					onSave={(tags) => {
						setTeamSong((state) => ({
							...state,
							songTypeTags: tags,
						}));
						popDisplayState();
					}}
				/>
			),
		},
	],
]);
const displayStateKeys = Array.from(displayStateComponents.keys());

export const AddCustomSong: React.FC<IProps> = ({ onClose }) => {
	const [displayStateStack, setDisplayStateStack] = useState<
		EAddCustomSongDisplayState[]
	>([EAddCustomSongDisplayState.ROOT]);
	const [displayState, setDisplayState] =
		useState<EAddCustomSongDisplayState>(
			getDisplayState(displayStateStack),
		);
	const [customTitle, setCustomTitle] = useState<string>('');
	const [teamSong, setTeamSong] = useState<CreateTeamSong>({});
	const [actionOpen, setActionOpen] = useState(false);
	const [firedTouchFirstStepEvent, setFiredTouchFirstStepEvent] =
		useState(false);
	const fireViewDiscardDraftEvent = useAnalyticsEvent(
		'Add_Custom_Song_View_Discard_Draft',
	);
	const fireAcceptDiscardDraftEvent = useAnalyticsEvent(
		'Add_Custom_Song_Accept_Discard_Draft',
	);
	const fireCancelDiscardDraftEvent = useAnalyticsEvent(
		'Add_Custom_Song_Cancel_Discard_Draft',
	);
	const fireCloseEvent = useAnalyticsEvent('Add_Custom_Song_Close');
	const fireTouchFirstStepEvent = useAnalyticsEvent(
		'Add_Custom_Song_Touch_First_Step',
	);

	const panelTitle = getNameForDisplayState(displayState);

	useEffect(() => {
		if (Object.keys(teamSong).length && !firedTouchFirstStepEvent) {
			fireTouchFirstStepEvent();
			setFiredTouchFirstStepEvent(true);
		}
	}, [teamSong, fireTouchFirstStepEvent, firedTouchFirstStepEvent]);

	useEffect(() => {
		setDisplayState(getDisplayState(displayStateStack));
	}, [displayStateStack]);

	const popDisplayState = useCallback(() => {
		if (displayStateStack.length > 1) {
			const newStack = [...displayStateStack];
			newStack.pop();
			setDisplayStateStack(newStack);
		}
	}, [displayStateStack]);

	const handleActionClose = () => {
		setActionOpen(false);
	};

	const handleCompleteClose = () => {
		setActionOpen(false);
		onClose();
		popDisplayState();
		setTeamSong({});
		setFiredTouchFirstStepEvent(false);
	};

	const handleClose = () => {
		if (Object.keys(teamSong).length) {
			fireViewDiscardDraftEvent();
			return setActionOpen(true);
		}
		fireCloseEvent();
		handleCompleteClose();
	};

	const setOpen = (value: boolean) => onClose();

	return (
		<AsaphDialog open={true} onClose={handleClose}>
			<div className={styles['song-modal-panel__toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderStart(),
					}}
					title={{
						needsFadeIn:
							!!panelTitle ||
							(displayState ===
								EAddCustomSongDisplayState.SONG_SETTINGS &&
								customTitle !== ''),
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						label:
							!isNil(customTitle) &&
							displayState ===
								EAddCustomSongDisplayState.SONG_SETTINGS
								? customTitle
								: panelTitle,
					}}
					trailingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderEnd(),
					}}
				/>
			</div>
			<div className={styles['song-modal-panel__toolbar-divider']}>
				<hr />
			</div>
			<section className={styles['song-modal-panel__content']}>
				{displayStateKeys.map((key) => {
					const displayStateComponent =
						displayStateComponents.get(key);
					if (!displayStateComponent) {
						return null;
					}
					const Component = displayStateComponent.component;

					if (key === EAddCustomSongDisplayState.ROOT) {
						return (
							<Fade
								key={key}
								in={displayState === key}
								timeout={PANEL_TRANSITION_TIMEOUT}
								mountOnEnter
								unmountOnExit
							>
								<div
									className={
										styles[
											'song-modal-panel__content-container'
										]
									}
								>
									<Component
										pushDisplayState={pushDisplayState}
										popDisplayState={popDisplayState}
										panelTransitionTimeout={
											PANEL_TRANSITION_TIMEOUT
										}
										teamSong={teamSong}
										setTeamSong={setTeamSong}
										setOpen={setOpen}
										setCustomTitle={setCustomTitle}
										handleCompleteClose={
											handleCompleteClose
										}
									/>
								</div>
							</Fade>
						);
					}

					return (
						<Slide
							key={key}
							direction="left"
							in={
								displayState === key ||
								displayStateStack.indexOf(key) >= 0
							}
							timeout={PANEL_TRANSITION_TIMEOUT}
							mountOnEnter
							unmountOnExit
						>
							<div
								className={
									styles[
										'song-modal-panel__content-container'
									]
								}
							>
								<Component
									pushDisplayState={pushDisplayState}
									popDisplayState={popDisplayState}
									panelTransitionTimeout={
										PANEL_TRANSITION_TIMEOUT
									}
									teamSong={teamSong}
									setTeamSong={setTeamSong}
									setOpen={setOpen}
									setCustomTitle={setCustomTitle}
									handleCompleteClose={handleCompleteClose}
								/>
							</div>
						</Slide>
					);
				})}
			</section>
			<ActionSheet
				open={actionOpen}
				title={
					"You have unsaved changes. Are you sure you want to delete the draft song that you've started?"
				}
				onClose={() => {
					fireCancelDiscardDraftEvent();
					handleActionClose();
				}}
			>
				<ActionSheetAction
					label={'Yes, delete draft'}
					onActionClick={() => {
						fireAcceptDiscardDraftEvent();
						handleCompleteClose();
					}}
					type={'danger'}
				/>
			</ActionSheet>
		</AsaphDialog>
	);

	function pushDisplayState(newState: EAddCustomSongDisplayState) {
		setDisplayStateStack([...displayStateStack, newState]);
	}

	function renderHeaderStart(): ReactNode {
		switch (displayState) {
			case EAddCustomSongDisplayState.ROOT:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Done"
						handlePress={handleClose}
					/>
				);
			default:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Back"
						handlePress={popDisplayState}
					/>
				);
		}
	}

	function renderHeaderEnd(): ReactNode {
		switch (displayState) {
			// case EAddCustomSongDisplayState.ROOT:
			// 	return (
			// 		<AsaphDialogHeaderButton
			// 			type="text"
			// 			label="Done"
			// 			handlePress={handleClose}
			// 		/>
			// 	);
			default:
				return null;
		}
	}
};

function getDisplayState(displayStateStack: EAddCustomSongDisplayState[]) {
	return displayStateStack.length
		? displayStateStack[displayStateStack.length - 1]
		: EAddCustomSongDisplayState.ROOT;
}

function getNameForDisplayState(
	displayState: EAddCustomSongDisplayState,
): string {
	const displayStateComponent = displayStateComponents.get(displayState);
	if (displayStateComponent) {
		return pathOr('', ['title'], displayStateComponent);
	}
	return '';
}
